import { useEffect, useMemo, useState } from "react";
import "./App.css";
import ManifestScreen from "./ManifestScreen";
import DeliveryScreen from "./DeliveryScreen";
import DeliveryOrderScreen from "./DeliveryOrderScreen";
import LoginScreen from "./LoginScreen";
import UsersScreen from "./UsersScreen";
import ReportsScreen from "./ReportsScreen";

import { api } from "./api/api";

function readPerms() {
  try {
    return JSON.parse(localStorage.getItem("permissions") || "[]");
  } catch {
    return [];
  }
}

function has(perms, code) {
  return Array.isArray(perms) && perms.includes(code);
}

const formatMoney = (n) => {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(num);
};

function App() {
  const [activeTab, setActiveTab] = useState("deliveryOrder");

  // auth state
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [permissions, setPermissions] = useState(readPerms());
  const [authMsg, setAuthMsg] = useState("");

  // services state (كما هو)
  const [serviceName, setServiceName] = useState("");
  const [price, setPrice] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [editingService, setEditingService] = useState(null);

  // ✅ NEW: Delivery Order Rates state
  const [rateMinKg, setRateMinKg] = useState("");
  const [rateMaxKg, setRateMaxKg] = useState("");
  const [ratePrice, setRatePrice] = useState("");
  const [rates, setRates] = useState([]);
  const [ratesMessage, setRatesMessage] = useState("");
  const [editingRate, setEditingRate] = useState(null);

  // ✅ استقبل forced logout من api.js
  useEffect(() => {
    const handler = (e) => {
      setToken("");
      setPermissions([]);
      setAuthMsg(e.detail || "Logged out because another device has logged in.");
    };
    window.addEventListener("auth:forcedLogout", handler);
    return () => window.removeEventListener("auth:forcedLogout", handler);
  }, []);

  // ✅ لو عندك token جرّب /me لتأكيد الجلسة + تحديث permissions
  useEffect(() => {
    const boot = async () => {
      if (!token) return;

      try {
        const data = await api.auth.me();
        const perms = data?.user?.permissions || data?.permissions || [];
        setPermissions(perms);
        localStorage.setItem("permissions", JSON.stringify(perms));
      } catch (e) {
        api.auth.clear();
        setToken("");
        setPermissions([]);
      }
    };

    boot();
  }, [token]);

  const logout = async () => {
    try {
      if (token) await api.auth.logout();
    } catch {}
    api.auth.clear();
    setToken("");
    setPermissions([]);
    setAuthMsg("");
  };

  const allowedTabs = useMemo(() => {
    return {
      services: has(permissions, "SERVICES_EDIT") || has(permissions, "USERS_ADMIN"),
      manifest:
        has(permissions, "MANIFEST_ENTRY") ||
        has(permissions, "MANIFEST_CREATE") ||
        has(permissions, "MANIFEST_SEARCH") ||
        has(permissions, "MANIFEST_EDIT"),
      delivery: has(permissions, "BOL_INVOICE"),
      deliveryOrder: has(permissions, "BOL_INVOICE"),
      users: has(permissions, "USERS_ADMIN"),
      reports: has(permissions, "USERS_ADMIN"),
    };
  }, [permissions]);

  // ✅ لو التاب الحالي غير مسموح، انقله لأول تاب مسموح
  useEffect(() => {
    if (!token) return;
    const order = ["deliveryOrder", "delivery", "manifest", "services"];

    if (!allowedTabs[activeTab]) {
      const next = order.find((t) => allowedTabs[t]);
      setActiveTab(next || "services");
    }
  }, [token, allowedTabs, activeTab]);

  // ===== services (كما هو عندك) =====
  useEffect(() => {
    if (activeTab === "services" && allowedTabs.services) {
      fetchServices();
      fetchRates(); // ✅ NEW
    }
    // eslint-disable-next-line
  }, [activeTab, allowedTabs.services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await api.get("/services");
      const list =
        Array.isArray(res) ? res :
        Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.rows) ? res.rows :
        [];
      setServices(list);
    } catch (err) {
      console.error(err);
      setServices([]);
      setMessage("Error loading services");
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: fetch delivery order rates
  const fetchRates = async () => {
    try {
      const res = await api.get("/delivery-order-rates");
      const list =
        Array.isArray(res) ? res :
        Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.rows) ? res.rows :
        [];
      setRates(list);
    } catch (err) {
      console.error(err);
      setRates([]);
      setRatesMessage("Error loading delivery order rates");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!serviceName || !price) {
      setMessage("Please enter service name and price");
      return;
    }

    try {
      setLoading(true);
      if (editingService) {
        await api.put(`/services/${editingService.id}`, {
          name: serviceName,
          price: Number(price),
        });
        setMessage("Service updated successfully ✅");
      } else {
        await api.post("/services", {
          name: serviceName,
          price: Number(price),
        });
        setMessage("Service added successfully ✅");
      }

      setServiceName("");
      setPrice("");
      setEditingService(null);
      fetchServices();
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Server connection error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: submit rate
  const handleSubmitRate = async (e) => {
    e.preventDefault();
    setRatesMessage("");

    const min = Number(rateMinKg);
    const max = Number(rateMaxKg);
    const p = Number(ratePrice);

    if (!rateMinKg || !rateMaxKg || !ratePrice) {
      setRatesMessage("Please enter min KG, max KG and price");
      return;
    }
    if (Number.isNaN(min) || Number.isNaN(max) || Number.isNaN(p)) {
      setRatesMessage("Invalid numbers");
      return;
    }
    if (min <= 0 || max <= 0 || p < 0) {
      setRatesMessage("KG must be > 0 and price must be >= 0");
      return;
    }
    if (min > max) {
      setRatesMessage("Min KG must be <= Max KG");
      return;
    }

    try {
      setLoading(true);

if (editingRate) {
  await api.put(`/delivery-order-rates/${editingRate.id}`, {
    minKg: min,
    maxKg: max,
    deliveryPrice: p,
    // isActive: 1, // اختياري
  });
  setRatesMessage("Rate updated successfully ✅");
} else {
  await api.post("/delivery-order-rates", {
    minKg: min,
    maxKg: max,
    deliveryPrice: p,
    // isActive: 1, // اختياري
  });
  setRatesMessage("Rate added successfully ✅");
}


      setRateMinKg("");
      setRateMaxKg("");
      setRatePrice("");
      setEditingRate(null);
      fetchRates();
    } catch (err) {
      console.error(err);
      setRatesMessage(err.message || "Server connection error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      setLoading(true);
      await api.del(`/services/${id}`);
      fetchServices();
    } catch (err) {
      console.error(err);
      setMessage(err.message || "Error deleting service");
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: delete rate
  const handleDeleteRate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rate?")) return;
    try {
      setLoading(true);
      await api.del(`/delivery-order-rates/${id}`);
      fetchRates();
    } catch (err) {
      console.error(err);
      setRatesMessage(err.message || "Error deleting rate");
    } finally {
      setLoading(false);
    }
  };

  const handleEditService = (service) => {
    setServiceName(service.name);
    setPrice(service.price);
    setEditingService(service);
  };

  // ✅ NEW: edit rate
  const handleEditRate = (r) => {
    setRateMinKg(String(r.min_kg ?? r.minKg ?? ""));
    setRateMaxKg(String(r.max_kg ?? r.maxKg ?? ""));
    setRatePrice(String(r.delivery_price ?? ""));

    setEditingRate(r);
  };

  // ✅ لو غير مسجل دخول: اعرض Login
  if (!token) {
    return (
      <div className="App">
        <header className="App-header">
          {authMsg && <div className="message error">{authMsg}</div>}
          <LoginScreen
            onLoggedIn={(data) => {
              setToken(data.token);
              setPermissions(data.permissions || []);
              setAuthMsg("");
              const first = ["deliveryOrder", "delivery", "manifest", "services"]
                .find((t) => (t === "services" ? true : true));
              setActiveTab(first || "deliveryOrder");
            }}
          />
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
          <div className="tabs">
            {allowedTabs.services && (
              <button onClick={() => setActiveTab("services")} className={activeTab === "services" ? "active-tab" : ""}>
                Services
              </button>
            )}

            {allowedTabs.manifest && (
              <button onClick={() => setActiveTab("manifest")} className={activeTab === "manifest" ? "active-tab" : ""}>
                XML Manifests
              </button>
            )}

            {allowedTabs.delivery && (
              <button onClick={() => setActiveTab("delivery")} className={activeTab === "delivery" ? "active-tab" : ""}>
                Delivery
              </button>
            )}

            {allowedTabs.deliveryOrder && (
              <button onClick={() => setActiveTab("deliveryOrder")} className={activeTab === "deliveryOrder" ? "active-tab" : ""}>
                Delivery Order
              </button>
            )}

            {allowedTabs.reports && (
  <button
    onClick={() => setActiveTab("reports")}
    className={activeTab === "reports" ? "active-tab" : ""}
  >
    Reports
  </button>
)}


            {allowedTabs.users && (
              <button onClick={() => setActiveTab("users")} className={activeTab === "users" ? "active-tab" : ""}>
                Users
              </button>
            )}
          </div>

          <button type="button" className="secondary-btn" onClick={logout}>
            Logout
          </button>
        </div>

        {!allowedTabs.services && !allowedTabs.manifest && !allowedTabs.delivery && !allowedTabs.deliveryOrder && (
          <div className="message error" style={{ marginTop: "1rem" }}>
            لا توجد صلاحيات مخصصة لهذا المستخدم. تواصل مع المدير لإضافة الصلاحيات.
          </div>
        )}

        {/* Content */}
        {activeTab === "services" && allowedTabs.services && (
          <div className="services-wrap">
            <h2>Add New Service</h2>

            <form onSubmit={handleSubmit} className="services-form">
              <input
                type="text"
                placeholder="Service Name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
              />

              <input
                type="number"
                placeholder="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />

              <button type="submit" disabled={loading}>
                {loading ? "Saving..." : editingService ? "Update Service" : "Save Service"}
              </button>
            </form>

            {message && <p className="services-message">{message}</p>}

            <hr style={{ margin: "1.5rem 0" }} />

            <h3>Services List</h3>

            {loading && services.length === 0 && <p>Loading...</p>}
            {!loading && services.length === 0 && <p>No services found.</p>}

            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Service Name</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(services) ? services : []).map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>{formatMoney(s.price)}</td>
                      <td>
                        <div className="actions-cell">
                          <button onClick={() => handleEditService(s)}>Edit</button>
                          <button onClick={() => handleDeleteService(s.id)} className="danger-btn">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ✅ NEW SECTION: Delivery Order Rates */}
            <hr style={{ margin: "2rem 0" }} />

            <h2>Delivery Order Rates (KG Range)</h2>

            <form onSubmit={handleSubmitRate} className="services-form">
              <input
                type="number"
                placeholder="Min KG (e.g. 1)"
                value={rateMinKg}
                onChange={(e) => setRateMinKg(e.target.value)}
              />
              <input
                type="number"
                placeholder="Max KG (e.g. 100)"
                value={rateMaxKg}
                onChange={(e) => setRateMaxKg(e.target.value)}
              />
              <input
                type="number"
                placeholder="Delivery Price"
                value={ratePrice}
                onChange={(e) => setRatePrice(e.target.value)}
              />

              <button type="submit" disabled={loading}>
                {loading ? "Saving..." : editingRate ? "Update Rate" : "Save Rate"}
              </button>
            </form>

            {ratesMessage && <p className="services-message">{ratesMessage}</p>}

            <h3>Rates List</h3>

            {!loading && rates.length === 0 && <p>No rates found.</p>}

            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Range (KG)</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(rates) ? rates : []).map((r) => {
                    const min = r.min_kg ?? r.minKg ?? "";
                    const max = r.max_kg ?? r.maxKg ?? "";
                    return (
                      <tr key={r.id}>
                        <td>{min}-{max}</td>
                        <td>{formatMoney(r.delivery_price)}</td>

                        <td>
                          <div className="actions-cell">
                            <button onClick={() => handleEditRate(r)}>Edit</button>
                            <button onClick={() => handleDeleteRate(r.id)} className="danger-btn">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "manifest" && allowedTabs.manifest && (
          <ManifestScreen permissions={permissions} />
        )}
        {activeTab === "delivery" && allowedTabs.delivery && <DeliveryScreen />}
        {activeTab === "deliveryOrder" && allowedTabs.deliveryOrder && <DeliveryOrderScreen />}
        {activeTab === "users" && allowedTabs.users && <UsersScreen permissions={permissions} />}
        {activeTab === "reports" && allowedTabs.reports && <ReportsScreen />}

      </header>
    </div>
  );
}

export default App;
